import {
  BOOLEAN_SELECT_LIST_ITEMS,
  Box,
  Card,
  CrudMode,
  DataDisplay,
  Grid,
  Stack,
  Typography,
  ValidationErrors,
  formatBoolean,
  pn,
  useElementBreakpoints,
} from "@compass/core";
import { Select } from "components";
import {
  LkStudentDisplayField,
  LkStudentDisplayFieldId,
  Student,
  StudentEditResource,
  StudentViewResource,
} from "generated/openApi";
import produce from "immer";

export const Assignment = (props: {
  crudMode: CrudMode;
  student: Student;
  setStudent: (value: Student) => void;
  viewResource: StudentViewResource;
  editResource: StudentEditResource;
  validationErrors: ValidationErrors;
}) => {
  const { breakpointsRef, smallFieldBreakpoints } = useElementBreakpoints();
  const properties = props.viewResource.properties || [];
  const displayFields = props.viewResource.displayFields || [];

  const assignedUserDisplayField = displayFields.find((x) => x.id === LkStudentDisplayFieldId.AssignedUser);
  const unresponsiveDisplayField = displayFields.find((x) => x.id === LkStudentDisplayFieldId.Unresponsive);

  const showAssignment = assignedUserDisplayField || unresponsiveDisplayField;

  if (!showAssignment) return null;

  const assignedUserField = () => {
    const propertyName = pn<Student>("assignedUserId");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={assignedUserDisplayField?.displayName}>
        {props.student.assignedUser && (
          <>
            {props.student.assignedUser?.lastName}, {props.student.assignedUser?.firstName}
          </>
        )}
      </DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        label={assignedUserDisplayField?.displayName}
        required={assignedUserDisplayField?.required}
        errors={props.validationErrors[propertyName]}
        value={props.editResource.users?.find((x) => x.id === props.student.assignedUserId)}
        options={props.editResource.users}
        getOptionLabel={(option) => `${option.lastName}, ${option.firstName}`}
        onChange={(e, value) =>
          props.setStudent(
            produce(props.student, (draft) => {
              draft.assignedUserId = value?.id;
            })
          )
        }
      />
    );
  };

  const unresponsiveField = () => {
    const propertyName = pn<Student>("unresponsive");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={unresponsiveDisplayField?.displayName}>
        {formatBoolean(props.student.unresponsive)}
      </DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        label={unresponsiveDisplayField?.displayName}
        errors={props.validationErrors[propertyName]}
        value={BOOLEAN_SELECT_LIST_ITEMS.find((x) => x.id === props.student.unresponsive)}
        options={BOOLEAN_SELECT_LIST_ITEMS}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setStudent(
            produce(props.student, (draft) => {
              draft.unresponsive = value?.id;
            })
          )
        }
      />
    );
  };

  return (
    <Card>
      <Grid ref={breakpointsRef} container spacing="sm">
        {assignedUserDisplayField && (
          <Grid item xs={smallFieldBreakpoints}>
            {assignedUserField()}
          </Grid>
        )}

        {unresponsiveDisplayField && (
          <Grid item xs={smallFieldBreakpoints}>
            {unresponsiveField()}
          </Grid>
        )}
      </Grid>
    </Card>
  );
};
